import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Bell,
  Loader2,
  Eye,
  MousePointerClick,
  AlertCircle,
  Clock,
  Image as ImageIcon,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow, format } from "date-fns";

interface DeliveryMapping {
  delivery_id: number;
  rendered_title: string;
  rendered_body: string;
  status: string;
  action_url: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  topic: string | null;
  created_date: string | null;
  notification_deliveries: {
    id: number;
    template_id: number | null;
    image_url: string | null;
    created_at: string;
    notification_templates: {
      name: string;
      category: string;
    } | null;
  } | null;
}

const SELECT_FIELDS = `
  delivery_id,
  user_id,
  rendered_title,
  rendered_body,
  status,
  action_url,
  opened_at,
  clicked_at,
  topic,
  created_date,
  notification_deliveries(
    id,
    template_id,
    image_url,
    created_at,
    notification_templates(name, category)
  )
`;

async function fetchUserNotifications(userId: number): Promise<DeliveryMapping[]> {
  // 1. Get user's subscribed topics
  const { data: subs } = await supabase
    .from("user_topic_subscriptions")
    .select("topic")
    .eq("user_id", userId)
    .eq("is_subscribed", true);

  const subscribedTopics = (subs || []).map((s) => s.topic);

  // 2. Fetch direct notifications + topic broadcasts in parallel
  const [directResult, topicResult] = await Promise.all([
    // Direct / group notifications (user_id = this user)
    supabase
      .from("notification_delivery_mapping")
      .select(SELECT_FIELDS)
      .eq("user_id", userId)
      .order("delivery_id", { ascending: false })
      .limit(50),

    // Topic broadcasts the user is subscribed to (user_id IS NULL)
    subscribedTopics.length > 0
      ? supabase
          .from("notification_delivery_mapping")
          .select(SELECT_FIELDS)
          .is("user_id", null)
          .in("topic", subscribedTopics)
          .order("delivery_id", { ascending: false })
          .limit(50)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (directResult.error) throw directResult.error;
  if (topicResult.error) throw topicResult.error;

  // 3. Merge and sort by delivery_id descending
  const all = [
    ...((directResult.data || []) as unknown as DeliveryMapping[]),
    ...((topicResult.data || []) as unknown as DeliveryMapping[]),
  ].sort((a, b) => b.delivery_id - a.delivery_id);

  return all.slice(0, 50);
}

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pending: { label: "Pending", variant: "secondary" },
  sent: { label: "Sent", variant: "outline" },
  delivered: { label: "Delivered", variant: "default" },
  opened: { label: "Opened", variant: "default" },
  clicked: { label: "Clicked", variant: "default" },
  failed: { label: "Failed", variant: "destructive" },
};

interface UserNotificationsProps {
  userId: number;
  userName: string;
}

export default function UserNotifications({ userId, userName }: UserNotificationsProps) {
  const { data: notifications, isLoading, error } = useQuery({
    queryKey: ["user-notifications", userId],
    queryFn: () => fetchUserNotifications(userId),
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="h-8 w-8 mx-auto mb-2 text-destructive" />
          <p className="text-sm text-destructive">Failed to load notifications</p>
          <p className="text-xs text-muted-foreground mt-1">{(error as Error).message}</p>
        </CardContent>
      </Card>
    );
  }

  if (!notifications || notifications.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Bell className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">No notifications sent to {userName} yet</p>
        </CardContent>
      </Card>
    );
  }

  const stats = {
    total: notifications.length,
    opened: notifications.filter((n) => n.opened_at).length,
    clicked: notifications.filter((n) => n.clicked_at).length,
    failed: notifications.filter((n) => n.status === "failed").length,
  };

  return (
    <div className="space-y-4">
      {/* Stats summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-xs text-muted-foreground">Total Sent</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.opened}</div>
            <div className="text-xs text-muted-foreground">Opened</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.clicked}</div>
            <div className="text-xs text-muted-foreground">Clicked</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
            <div className="text-xs text-muted-foreground">Failed</div>
          </CardContent>
        </Card>
      </div>

      {/* Notification list */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Bell className="h-4 w-4" />
            Recent Notifications
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal ml-auto">
              Last {notifications.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {notifications.map((n, i) => {
              const createdAt = n.notification_deliveries?.created_at || n.created_date;
              const statusConfig = STATUS_CONFIG[n.status] || STATUS_CONFIG.pending;
              const templateName = n.notification_deliveries?.notification_templates?.name;
              const category = n.notification_deliveries?.notification_templates?.category;
              const hasImage = !!(n.notification_deliveries?.image_url);

              return (
                <div
                  key={`${n.delivery_id}-${i}`}
                  className="flex gap-3 p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                >
                  {/* Status indicator */}
                  <div className="pt-0.5 shrink-0">
                    {n.status === "failed" ? (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    ) : n.clicked_at ? (
                      <MousePointerClick className="h-4 w-4 text-blue-500" />
                    ) : n.opened_at ? (
                      <Eye className="h-4 w-4 text-green-500" />
                    ) : (
                      <Bell className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium leading-tight truncate">
                        {n.rendered_title || "Untitled"}
                      </p>
                      <Badge variant={statusConfig.variant} className="text-[10px] px-1.5 py-0 shrink-0">
                        {statusConfig.label}
                      </Badge>
                    </div>

                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {n.rendered_body}
                    </p>

                    {/* Meta row */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
                      {createdAt && (
                        <span className="flex items-center gap-1" title={format(new Date(createdAt), "PPpp")}>
                          <Clock className="h-3 w-3" />
                          {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
                        </span>
                      )}
                      {templateName && (
                        <span>
                          Template: {templateName}
                        </span>
                      )}
                      {category && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5">
                          {category}
                        </Badge>
                      )}
                      {n.topic && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5">
                          topic: {n.topic}
                        </Badge>
                      )}
                      {hasImage && (
                        <ImageIcon className="h-3 w-3" />
                      )}
                      {n.opened_at && (
                        <span className="text-green-600">
                          Opened {formatDistanceToNow(new Date(n.opened_at), { addSuffix: true })}
                        </span>
                      )}
                      {n.clicked_at && (
                        <span className="text-blue-600">
                          Clicked {formatDistanceToNow(new Date(n.clicked_at), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
